import { sendMessage } from '../../features/realtime/realtime.slice';
import { RealtimeMessageType } from '../../features/realtime/realtime.types';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { getUsername } from '../../features/realtime/realtime.selectors';
import { addRemovingParticipantTime } from '../../features/session/session.slice';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import { useHotpot } from '@doxyme/hotpot/react';
import { session } from '../../webrtc/session';

export type UseProviderActionsReturn = {
  /**
   * Removes one or more participants from the group call.
   */
  removeGroupCallParticipants: (ids: string[]) => Promise<void>;

  /**
   * Mutes a participant in the group call.
   */
  muteParticipant: (id: string) => Promise<void>;
  removeParticipantsAfterCallHangUp: (ids: string[]) => Promise<void>;
  removeOnHoldParticipants: (ids: string[]) => Promise<void>;
};

export const useProviderActions = (): UseProviderActionsReturn => {
  const dispatch = useAppDispatch();
  const username = useAppSelector(getUsername) ?? '';

  // for hotpot
  const hotpot = useHotpot();
  const membersMap = useAppSelector(getAllMembersMap);

  const removeGroupCallParticipants = async (ids: string[]): Promise<void> => {
    return removeParticipants(
      ids,
      RealtimeMessageType.GroupCallParticipantLeft,
    );
  };

  const removeParticipants = async (
    ids: string[],
    messageType: RealtimeMessageType,
  ): Promise<void> => {
    dispatch(addRemovingParticipantTime(ids));
    return Promise.allSettled(
      ids.map(async (participantId) => {
        session.removeSipParticipant(participantId);
        dispatch(
          sendMessage({
            id: participantId,
            message: {
              messageType,
            },
          }),
        );
      }),
    ) as unknown as Promise<void>;
  };

  const removeParticipantsAfterCallHangUp = async (
    ids: string[],
  ): Promise<void> => {
    return removeParticipants(
      ids,
      RealtimeMessageType.RemoveParticipantsAfterCallHangUp,
    );
  };

  const removeOnHoldParticipants = async (ids: string[]): Promise<void> => {
    // for hotpot
    void Promise.all(
      ids.map(async (id) => {
        const dbSessionId = membersMap[id]?.state?.dbSessionId;
        if (dbSessionId) {
          try {
            await hotpot?.visits.call.leaveByRealtimeId({
              realtimeId: id,
              sessionId: dbSessionId,
            });
            void hotpot?.visits.endCallByRealtimeId({
              realtimeId: id,
              sessionId: dbSessionId,
            });
          } catch (_error) {
            // error will be logged by hotpot
          }
        }
      }),
    );

    return removeParticipants(
      ids,
      RealtimeMessageType.RemoveOnHoldParticipants,
    );
  };

  const muteParticipant = async (id: string): Promise<void> => {
    session.muteSipParticipant(id);
    dispatch(
      sendMessage({
        id,
        message: {
          messageType: RealtimeMessageType.ParticipantMuted,
          payload: username,
        },
      }),
    );
  };

  return {
    removeGroupCallParticipants,
    muteParticipant,
    removeParticipantsAfterCallHangUp,
    removeOnHoldParticipants,
  };
};
